import React, { useEffect, useState } from "react";
import {
  Crown, ShieldCheck, HardHat, Truck, Briefcase, Wrench,
  Activity, AlertTriangle, ClipboardList, FileText, Users, CloudRain, FolderTree,
} from "lucide-react";

/**
 * RoleWelcomeStrip — operational header that adapts to the demo identity
 * stored in localStorage as `forgeops.demo.identity`. Each role reads
 * differently — purpose-built for THAT person.
 *
 * Tone per role:
 *   Admin/Owner  — operational command, system-wide
 *   Superintendent — field leadership under pressure
 *   PM           — control + coordination
 *   Safety       — risk management + compliance control
 *   Shop         — keeping operations alive
 *   Foreman/Operator — field execution (compact strip)
 *
 * Restraint over flash. Three metric-callouts + a one-line operational
 * focus sentence. NOT a giant header.
 */
const ROLE_CONFIG = {
  // Admin / Owner — system-wide command
  "admin@forgeops.demo": {
    icon: Crown,
    chip: "EXECUTIVE · SYSTEM-WIDE",
    title: (n) => `${n}, command surface online.`,
    sub:   "System-wide visibility · routing · users · records · controls.",
    accent: "#FF6A00",
    metrics: [
      { icon: FolderTree, label: "Active jobs",     v: "4" },
      { icon: Activity,   label: "Records today",   v: "30" },
      { icon: AlertTriangle, label: "Open incidents", v: "2" },
    ],
  },
  // Superintendent — field leadership
  "super@forgeops.demo": {
    icon: ShieldCheck,
    chip: "FIELD LEADERSHIP · OPERATIONAL PRESSURE",
    title: (n) => `${n}, here's the field this morning.`,
    sub:   "Production · crews · weather · closures · equipment status — your work, by job.",
    accent: "#FF6A00",
    metrics: [
      { icon: HardHat,    label: "Manpower on shift", v: "48" },
      { icon: Truck,      label: "Equipment OOS",     v: "5/8" },
      { icon: CloudRain,  label: "Weather impacts",   v: "1.4 hr" },
    ],
  },
  // Project Manager
  "pm@forgeops.demo": {
    icon: Briefcase,
    chip: "PROJECT MANAGEMENT · COORDINATION CONTROL",
    title: (n) => `${n}, today's reports + routing queue.`,
    sub:   "Reports · approvals · RFIs · escalations · client-facing accountability.",
    accent: "#FF6A00",
    metrics: [
      { icon: ClipboardList, label: "Reports to review", v: "10" },
      { icon: FileText,      label: "Pending approvals", v: "3" },
      { icon: AlertTriangle, label: "Escalations open",  v: "2" },
    ],
  },
  // Safety
  "safety@forgeops.demo": {
    icon: ShieldCheck,
    chip: "SAFETY · COMPLIANCE CONTROL",
    title: (n) => `${n}, risk surface this morning.`,
    sub:   "Incidents · observations · corrective actions · OSHA-defensible records.",
    accent: "#FF6A00",
    metrics: [
      { icon: AlertTriangle, label: "Incidents open",   v: "2" },
      { icon: Users,         label: "Toolbox · 7d",      v: "22" },
      { icon: ClipboardList, label: "Corrective actions", v: "5" },
    ],
  },
  // Shop
  "shop@forgeops.demo": {
    icon: Wrench,
    chip: "SHOP · KEEPING OPERATIONS ALIVE",
    title: (n) => `${n}, the shop queue right now.`,
    sub:   "Repairs · downtime · failed inspections · OOS clearance · readiness.",
    accent: "#FF6A00",
    metrics: [
      { icon: Truck,         label: "Out of service",   v: "5" },
      { icon: Wrench,        label: "Repairs in queue", v: "11" },
      { icon: ClipboardList, label: "Pre-Op fails 24h", v: "3" },
    ],
  },
  // Foreman
  "foreman@forgeops.demo": {
    icon: HardHat,
    chip: "FOREMAN · FIELD EXECUTION",
    title: (n) => `${n}, ready to run the day.`,
    sub:   "Daily report · JHP ack · toolbox talk — straight from the truck.",
    accent: "#FF6A00",
    metrics: [
      { icon: ClipboardList, label: "Daily report",  v: "due" },
      { icon: Users,         label: "Toolbox talk",  v: "due" },
      { icon: FolderTree,    label: "Today's job",   v: "FO-1003" },
    ],
  },
  // Operator
  "operator@forgeops.demo": {
    icon: Truck,
    chip: "OPERATOR · UNIT READINESS",
    title: (n) => `${n}, walk-around first.`,
    sub:   "Pre-Op walk-around · OOS lockout · parts request — one scan opens the form.",
    accent: "#FF6A00",
    metrics: [
      { icon: Truck,         label: "Assigned unit", v: "EX-210" },
      { icon: ClipboardList, label: "Pre-Op",        v: "due" },
      { icon: Wrench,        label: "Parts on order", v: "1" },
    ],
  },
};

const FALLBACK = ROLE_CONFIG["admin@forgeops.demo"];

export default function RoleWelcomeStrip() {
  const [identity, setIdentity] = useState(null);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("forgeops.demo.identity");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.email) setIdentity(parsed);
    } catch { /* */ }
  }, []);

  if (!identity) return null;
  const cfg = ROLE_CONFIG[identity.email] || FALLBACK;
  const Icon = cfg.icon;
  const name = identity.name || "Operator";

  return (
    <div
      data-testid="role-welcome-strip"
      data-role={identity.email}
      className="rounded-xl border-2 mb-6 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 0% 0%, rgba(255,106,0,0.10), transparent 60%), #0B0B0B",
        borderColor: "#1F1F1F",
      }}
    >
      <div className="px-4 sm:px-6 py-4 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
        {/* Left — identity + tagline */}
        <div className="lg:col-span-7 flex items-start gap-3 min-w-0">
          <span
            className="inline-flex items-center justify-center w-11 h-11 rounded-md shrink-0"
            style={{
              background: "rgba(255,106,0,0.15)",
              color: cfg.accent,
              border: `1px solid ${cfg.accent}66`,
            }}
          >
            <Icon className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: cfg.accent }}>
              {cfg.chip}
            </div>
            <h2 className="font-display text-lg sm:text-xl font-black tracking-tight text-white leading-tight truncate">
              {cfg.title(name)}
            </h2>
            <p className="text-[12px] sm:text-sm leading-snug truncate" style={{ color: "#BDBDBD" }}>
              {cfg.sub}
            </p>
          </div>
        </div>

        {/* Right — 3 role-specific metrics */}
        <div className="lg:col-span-5 grid grid-cols-3 gap-px rounded overflow-hidden" style={{ background: "#1A1A1A" }}>
          {cfg.metrics.map((m, i) => {
            const MIcon = m.icon;
            return (
              <div
                key={i}
                className="px-2.5 sm:px-3 py-2 flex items-center gap-2"
                style={{ background: "#0F0F0F" }}
                data-testid={`rws-metric-${i}`}
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded shrink-0"
                  style={{ background: "rgba(255,106,0,0.10)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
                >
                  <MIcon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.2em] truncate" style={{ color: "#BDBDBD" }}>
                    {m.label}
                  </div>
                  <div className="font-display text-sm sm:text-base font-black text-white leading-none truncate">
                    {m.v}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
